import { ReviewBooleanIcon } from '../components';
import { DataTable, CellSort, EmptyRowsFallback } from '@shared/ui';
import { Flex, Text } from '@mantine/core';
import { formatRatingValue } from '@shared/utils';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useFeatureFlag } from '@packages/feature-flags/react';

export type ReviewsTableRow = {
  id: number;
  reviewAt: string | null;
  ratingTotal: number | null;
  interviewDone?: boolean | null;
  isHired: boolean | null;
  vacancy: { name: string };
  company: { name: string };
  candidate?: {
    firstname: string | null;
    lastname: string | null;
  };
};

type ReviewsTableProps = {
  reviews: ReviewsTableRow[];
  totalRecords?: number;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  recordsPerPageOptions?: number[];
  onRecordsPerPageChange?: (pageSize: number) => void;
  setPageSize: (pageSize: number) => void;
  isFilledFilters: boolean;
  onSortChange: (field: string, direction?: 'asc' | 'desc') => void;
  currentSort: Record<string, 'asc' | 'desc'>;
  isLoading: boolean;
  onRowClick: (review: ReviewsTableRow) => void;
  /** company-first = отзывы компаний; candidate-first = отзывы кандидатов */
  columnOrder: 'company-first' | 'candidate-first';
  emptySubDesc?: string;
};

const getCandidateName = (review: ReviewsTableRow) =>
  [review.candidate?.lastname, review.candidate?.firstname]
    .filter(Boolean)
    .join(' ') || '-';

export const ReviewsTable = ({
  reviews,
  totalRecords,
  page,
  setPage,
  pageSize,
  recordsPerPageOptions,
  onRecordsPerPageChange,
  setPageSize,
  isFilledFilters,
  onSortChange,
  currentSort,
  isLoading,
  onRowClick,
  columnOrder,
  emptySubDesc = '',
}: ReviewsTableProps) => {
  const ratingsAndReviewsEnabled = useFeatureFlag('ratingsAndReviews');

  const columns = useMemo(() => {
    const idCol = {
      accessor: 'id',
      title: (
        <CellSort
          label='ID'
          onSort={dir => onSortChange('id', dir)}
          currentDirection={currentSort.id}
        />
      ),
    };

    const reviewAtCol = {
      accessor: 'reviewAt',
      title: (
        <CellSort
          label='Дата и время отзыва'
          onSort={dir => onSortChange('reviewAt', dir)}
          currentDirection={currentSort.reviewAt}
        />
      ),
      render: (record: ReviewsTableRow) => (
        <Text size='sm'>
          {record.reviewAt
            ? dayjs(record.reviewAt).format('DD.MM.YYYY HH:mm')
            : '-'}
        </Text>
      ),
    };

    const ratingCol = {
      accessor: 'ratingTotal',
      title: (
        <CellSort
          label='Оценка'
          onSort={dir => onSortChange('ratingTotal', dir)}
          currentDirection={currentSort.ratingTotal}
        />
      ),
      render: (record: ReviewsTableRow) => (
        <Text size='sm'>
          {record.ratingTotal != null
            ? formatRatingValue(record.ratingTotal)
            : '-'}
        </Text>
      ),
    };

    const vacancyCol = {
      accessor: 'vacancy.name',
      title: (
        <CellSort
          label='Вакансия'
          onSort={dir => onSortChange('vacancy.name', dir)}
          currentDirection={currentSort['vacancy.name']}
        />
      ),
      render: (record: ReviewsTableRow) => (
        <Text size='sm'>{record.vacancy.name}</Text>
      ),
    };

    const companyCol = {
      accessor: 'company.name',
      title: (
        <CellSort
          label='Компания'
          onSort={dir => onSortChange('company.name', dir)}
          currentDirection={currentSort['company.name']}
        />
      ),
      render: (record: ReviewsTableRow) => (
        <Text size='sm'>{record.company.name}</Text>
      ),
    };

    const candidateCol = {
      accessor: 'candidate',
      title: 'Кандидат',
      render: (record: ReviewsTableRow) => (
        <Text size='sm'>{getCandidateName(record)}</Text>
      ),
    };

    const interviewCol = {
      accessor: 'interviewDone',
      width: 186,
      title: 'Собеседование',
      render: (record: ReviewsTableRow) => (
        <Flex>
          <ReviewBooleanIcon value={record.interviewDone} />
        </Flex>
      ),
    };

    const hiredCol = {
      accessor: 'isHired',
      width: 130,
      title: 'Нанят',
      render: (record: ReviewsTableRow) => (
        <Flex>
          <ReviewBooleanIcon value={record.isHired} />
        </Flex>
      ),
    };

    if (columnOrder === 'company-first') {
      return [
        idCol,
        companyCol,
        reviewAtCol,
        ...(ratingsAndReviewsEnabled ? [ratingCol] : []),
        vacancyCol,
        candidateCol,
        interviewCol,
        hiredCol,
      ];
    }

    return [
      idCol,
      candidateCol,
      reviewAtCol,
      ...(ratingsAndReviewsEnabled ? [ratingCol] : []),
      vacancyCol,
      companyCol,
      interviewCol,
      hiredCol,
    ];
  }, [ratingsAndReviewsEnabled, columnOrder, currentSort, onSortChange]);

  const emptyState = useMemo(() => {
    if (isFilledFilters) {
      return {
        desc: 'К сожалению, совпадений не найдено',
        subDesc:
          'Попробуйте изменить параметры фильтров. Чтобы вернуться ко всему списку, сбросьте фильтр.',
      };
    }

    return {
      desc: 'Список отзывов пуст',
      subDesc: emptySubDesc,
    };
  }, [emptySubDesc, isFilledFilters]);

  return (
    <DataTable<ReviewsTableRow>
      fetching={isLoading}
      columns={columns}
      records={reviews}
      onRowClick={({ record }) => onRowClick(record)}
      page={page}
      setPage={setPage}
      recordsPerPageOptions={recordsPerPageOptions}
      onRecordsPerPageChange={onRecordsPerPageChange}
      pageSize={pageSize}
      setPageSize={setPageSize}
      recordsPerPage={pageSize}
      onPageChange={setPage}
      totalRecords={totalRecords}
      highlightOnHover
      emptyState={
        <EmptyRowsFallback
          desc={emptyState.desc}
          subDesc={emptyState.subDesc}
        />
      }
    />
  );
};
